import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { BABOOR_ENVIRONMENT } from '@baboor/baboor-core'
import { ApiResponse } from '@baboor/common-models'
import { Observable } from 'rxjs'

export interface SuperAdminOverview {
    totalShops: number
    totalUsers: number
}

export interface SuperAdminShop {
    id: string
    name: string
    email: string | null
    phone: string | null
    address: string | null
    city: string | null
    country: string | null
    verifiedOn: string | null
    createdAt: string | null
    status: 'verified' | 'unverified'
    ownerName: string | null
    ownerEmail: string | null
}

export interface SuperAdminUser {
    id: string
    firstName: string
    lastName: string
    email: string | null
    phone: string | null
    status: 'active' | 'inactive' | 'banned'
    createdAt: string | null
    role: string
}

export interface SuperAdminNewShop {
    id: string
    name: string
    ownerName: string | null
    ownerEmail: string | null
    ownerPhone?: string | null
    city?: string | null
    state?: string | null
    postalCode?: string | null
    onboardedAt: string
}

export interface SuperAdminListParams {
    search?: string
    page?: number
    size?: number
}

/**
 * API service for the Super Admin dashboard (/admin). Every endpoint is
 * authorized server-side via checkToken + isSuperAdmin middleware; this
 * service only performs typed HTTP access.
 */
@Injectable({ providedIn: 'root' })
export class SuperAdminApiService {
    private http = inject(HttpClient)
    private env = inject(BABOOR_ENVIRONMENT)
    private apiUrl = `${this.env.apiUrl}/v1/super-admin`

    getOverview(): Observable<ApiResponse<SuperAdminOverview>> {
        return this.http.get<ApiResponse<SuperAdminOverview>>(
            `${this.apiUrl}/overview`,
        )
    }

    getShops(
        params?: SuperAdminListParams,
    ): Observable<ApiResponse<SuperAdminShop[]>> {
        return this.http.get<ApiResponse<SuperAdminShop[]>>(
            `${this.apiUrl}/shops`,
            { params: this.toHttpParams(params) },
        )
    }

    getUsers(
        params?: SuperAdminListParams,
    ): Observable<ApiResponse<SuperAdminUser[]>> {
        return this.http.get<ApiResponse<SuperAdminUser[]>>(
            `${this.apiUrl}/users`,
            { params: this.toHttpParams(params) },
        )
    }

    getNewShops(): Observable<ApiResponse<SuperAdminNewShop[]>> {
        return this.http.get<ApiResponse<SuperAdminNewShop[]>>(
            `${this.apiUrl}/new-shops`,
        )
    }

    private toHttpParams(params?: SuperAdminListParams): HttpParams {
        let httpParams = new HttpParams()
        if (params?.search) {
            httpParams = httpParams.set('search', params.search)
        }
        if (params?.page !== undefined) {
            httpParams = httpParams.set('page', params.page)
        }
        if (params?.size !== undefined) {
            httpParams = httpParams.set('size', params.size)
        }
        return httpParams
    }
}
